import type { AxiosError } from "axios";
import { getAnchorPlanAxiosPrefix } from "@/utils/anchor-plan/helpers";
import axiosInstance, { clearCsrfToken, isApiCrossOrigin } from "./axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost";

/** Резерв, якщо Laravel-сесія втратила redirect під час Google OAuth (різні порти / cookie). */
export const OAUTH_POST_LOGIN_REDIRECT_KEY = "oauth_post_login_redirect";

/** Знімає протухлий Bearer, якщо Anchor Plan API не бачить користувача (публічні GET + cookie). */
export async function clearStaleBearerForAnchorPlan(): Promise<void> {
  if (!localStorage.getItem("auth_token")) {
    return;
  }
  // На prod з api.* без Bearer /auth/me завжди user:null — не знімати валідний токен.
  if (isApiCrossOrigin()) {
    return;
  }
  try {
    const { data } = await axiosInstance.get(
      `${getAnchorPlanAxiosPrefix()}/auth/me`,
    );
    if (data?.user == null) {
      localStorage.removeItem("auth_token");
    }
  } catch {
    localStorage.removeItem("auth_token");
  }
}

export const authService = {
  /**
   * Якщо в localStorage лежить протухлий Bearer, перший запит отримає 401 і інтерсептор зніме токен.
   * Тоді користувач може лишитись з валідною Sanctum-сесією (cookie) — повторюємо запит без Bearer,
   * інакше списки на кшталт Anchor Plan приходять як для гостя (порожньо), поки не перелогінитись.
   */
  async getCurrentUser() {
    const hadBearer = !!localStorage.getItem("auth_token");
    try {
      const response = await axiosInstance.get("/api/user");
      return response.data;
    } catch (error) {
      const status = (error as AxiosError).response?.status;
      if (status === 401 && hadBearer) {
        localStorage.removeItem("auth_token");
        try {
          await axiosInstance.get("/sanctum/csrf-cookie");
          const response = await axiosInstance.get("/api/user");
          return response.data;
        } catch {
          return null;
        }
      }
      console.error("Failed to get user:", error);
      return null;
    }
  },

  async logout() {
    try {
      await axiosInstance.post("/logout");
      // Очищаем кэшированный CSRF токен после logout
      clearCsrfToken();
    } catch (error) {
      console.error("Logout error:", error);
      // Даже при ошибке очищаем CSRF токен
      clearCsrfToken();
      throw error;
    }
  },

  /**
   * @param redirectPath — шлях Vue після OAuth (наприклад /app/anchor-plan/), без хоста
   */
  getLoginUrl(redirectPath?: string) {
    const base = `${API_URL}/auth/google`;
    if (!redirectPath) {
      return base;
    }
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(OAUTH_POST_LOGIN_REDIRECT_KEY, redirectPath);
      }
    } catch {
      /* private mode */
    }
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}redirect=${encodeURIComponent(redirectPath)}`;
  },
};
